import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Group, Panel } from 'react-resizable-panels';
import {
  useTheme,
  useMediaQuery,
  Box,
  Chip,
  Stack,
  MenuItem,
  TextField,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import useAuthCheck from 'hooks/useAuthCheck';
import {
  useGetSeasonsQuery,
  useGetScheduledFlightsQuery,
  useGetAirportsQuery,
} from 'features/apiSlice';
import { LAYOUT_HEIGHT } from 'state/constants';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import ResizeSeparator from 'components/ResizeSeparator';
import NetworkMap from './NetworkMap';
import RouteSidebar from './RouteSidebar';
import { DAY_LABELS, AC_PALETTE } from './constants';


const NetworkPlanner = () => {
  useAuthCheck();
  const params  = useSelector((state) => state.params);
  const theme   = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedSeasonId, setSelectedSeasonId] = useState('');
  const [filterDay, setFilterDay]               = useState('ALL');
  const [selectedRoute, setSelectedRoute]       = useState(null);
  const [splitSize, setSplitSize]               = useState(72);

  const debounceTimer = useRef(null);
  const handleLayoutChange = useCallback((obj) => {
    const sizes = Object.values(obj);
    if (!sizes.length) return;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => setSplitSize(sizes[0]), 200);
  }, []);

  const mapCenter = useMemo(() => {
    const val = params?.find((p) => p.param === 'MAP_CENTER_DEFAULT')?.value;
    if (!val) return [15.0, 106.0];
    const [lat, lng] = val.split(',').map(Number);
    return [lat || 15.0, lng || 106.0];
  }, [params]);

  // ── Data ──────────────────────────────────────────────────────────
  const { data: seasonsData } = useGetSeasonsQuery({ limit: 200 });
  const seasons = useMemo(() => seasonsData?.data || [], [seasonsData]);

  useEffect(() => {
    if (!selectedSeasonId && seasons.length > 0) {
      const active = seasons.find((s) => s.status === 'active') || seasons[0];
      if (active) setSelectedSeasonId(active._id);
    }
  }, [seasons, selectedSeasonId]);

  const { data: flightData, isLoading } = useGetScheduledFlightsQuery(
    { seasonId: selectedSeasonId, limit: 1000 },
    { skip: !selectedSeasonId },
  );
  const allFlights = useMemo(() => flightData?.data || [], [flightData]);

  const { data: airportsData } = useGetAirportsQuery();
  const airportMap = useMemo(() => {
    const arr = airportsData || [];
    return Object.fromEntries(arr.filter((a) => a.iataCode).map((a) => [a.iataCode, a]));
  }, [airportsData]);

  // ── Derived ───────────────────────────────────────────────────────
  const acTypeColorMap = useMemo(() => {
    const types = [...new Set(allFlights.map((f) => f.acType).filter(Boolean))];
    return Object.fromEntries(types.map((t, i) => [t, AC_PALETTE[i % AC_PALETTE.length]]));
  }, [allFlights]);

  const routes = useMemo(() => {
    const map = new Map();
    for (const f of allFlights) {
      if (f.status === 'cancelled') continue;
      if (filterDay !== 'ALL' && !f.daysOfOperation?.includes(filterDay)) continue;
      const key = `${f.dep}-${f.arr}`;
      if (!map.has(key)) {
        map.set(key, { key, dep: f.dep, arr: f.arr, sectorType: f.sectorType, totalFreq: 0, flights: [], acTypes: new Set() });
      }
      const r = map.get(key);
      r.totalFreq += f.frequency || 1;
      r.acTypes.add(f.acType || 'N/A');
      r.flights.push(f);
    }
    return Array.from(map.values()).sort((a, b) => b.totalFreq - a.totalFreq);
  }, [allFlights, filterDay]);

  const usedAirports = useMemo(() => {
    const codes = new Set();
    for (const r of routes) { codes.add(r.dep); codes.add(r.arr); }
    return [...codes].map((c) => airportMap[c]).filter(Boolean);
  }, [routes, airportMap]);

  const getCoords = useCallback((code) => {
    const a = airportMap[code];
    if (!a?.latitude || !a?.longitude) return null;
    return [parseFloat(a.latitude), parseFloat(a.longitude)];
  }, [airportMap]);

  const maxFreq = useMemo(() => Math.max(...routes.map((r) => r.totalFreq), 1), [routes]);

  const selectedRouteFlights = useMemo(() => {
    if (!selectedRoute) return [];
    return routes.find((r) => r.key === selectedRoute)?.flights || [];
  }, [routes, selectedRoute]);

  const stats = useMemo(() => ({
    totalRoutes:        routes.length,
    totalWeeklyFlights: routes.reduce((s, r) => s + r.totalFreq, 0),
  }), [routes]);

  // ── Shared sub-components ─────────────────────────────────────────
  const mapNode = (
    <NetworkMap
      center={mapCenter}
      routes={routes}
      usedAirports={usedAirports}
      selectedRoute={selectedRoute}
      onSelectRoute={setSelectedRoute}
      acTypeColorMap={acTypeColorMap}
      maxFreq={maxFreq}
      getCoords={getCoords}
      splitSize={splitSize}
    />
  );

  const sidebarNode = (
    <RouteSidebar
      routes={routes}
      selectedRoute={selectedRoute}
      onSelectRoute={setSelectedRoute}
      selectedRouteFlights={selectedRouteFlights}
      acTypeColorMap={acTypeColorMap}
      selectedSeasonId={selectedSeasonId}
    />
  );

  // ── Render ────────────────────────────────────────────────────────
  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header
          title="Network Planner"
          subtitle="Route Map"
        />
        <Box display="flex" alignItems="center" gap={1}>
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            <Chip label={`${stats.totalRoutes} routes`} size="small" />
            <Chip label={`${stats.totalWeeklyFlights}x weekly`} size="small" variant="outlined" />
          </Stack>
          <TextField
            select
            size="small"
            label="Season"
            value={selectedSeasonId}
            onChange={(e) => setSelectedSeasonId(e.target.value)}
            slotProps={{
              select: {
                renderValue: (selected) =>
                  seasons.find((s) => s._id === selected)?.seasonCode || '',
              },
            }}
          >
            {seasons.map((s) => (
              <MenuItem key={s._id} value={s._id}>
                {s.seasonCode} - {s.seasonName}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </FlexBetween>

      <Box 
        display="grid" 
        gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
        gap={2}
      >
        <Box 
          sx={{ 
            gridColumn: 'span 12', 
            height: LAYOUT_HEIGHT, 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 1 
          }}
        >

          {/* Filters */}
          <Box
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            gap={1}
          >
            <ToggleButtonGroup 
              exclusive 
              size="small" 
              value={filterDay}
              onChange={(_, v) => v && setFilterDay(v)}
            >
              <ToggleButton value="ALL" sx={{ px: 1, fontSize: '0.7rem' }}>
                ALL
              </ToggleButton>
              {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                <ToggleButton 
                  key={d} 
                  value={String(d)} 
                  sx={{ px: 1, fontSize: '0.7rem' }}
                >
                  {DAY_LABELS[d].slice(0, 2)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {/* Map + Sidebar */}
          <Box flex={1}>
            {isLoading ? (
              <FlexBetween height="100%">
                <CircularProgress />
              </FlexBetween>
            ) : isMobile ? (
              <Box 
                height="100%" 
                display="flex" 
                flexDirection="column" 
                gap={1}
              >
                <Box 
                  sx={{ 
                    flex: '0 0 60%', 
                    borderRadius: 1, 
                    border: `1px solid ${theme.palette.divider}`, 
                    overflow: 'hidden' 
                  }}
                >
                  {mapNode}
                </Box>
                <Box 
                  sx={{ 
                    flex: 1, 
                    borderRadius: 1, 
                    border: `1px solid ${theme.palette.divider}`, 
                    overflow: 'hidden' 
                  }}
                >
                  {sidebarNode}
                </Box>
              </Box>
            ) : (
              <Group 
                direction="horizontal" 
                style={{ height: '100%' }} 
                onLayoutChange={handleLayoutChange}
              >
                <Panel 
                  defaultSize={splitSize} 
                  minSize={0}
                >
                  <Box 
                    sx={{ 
                      height: '100%', 
                      borderRadius: 1, 
                      border: `1px solid ${theme.palette.divider}`, 
                      overflow: 'hidden' 
                    }}
                  >
                    {mapNode}
                  </Box>
                </Panel>

                <ResizeSeparator />

                <Panel 
                  defaultSize={100 - splitSize} 
                  minSize={15}
                >
                  <Box 
                    sx={{ 
                      height: '100%', 
                      borderRadius: 1, 
                      border: `1px solid ${theme.palette.divider}`, 
                      overflow: 'hidden' 
                    }}
                  >
                    {sidebarNode}
                  </Box>
                </Panel>
              </Group>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default NetworkPlanner;
